import React, { FC, ReactElement, ReactNode } from "react"
import { Trans } from "react-i18next"
import { TFunction, TOptions } from "i18next"
import {
    EnhancedTranslatorProps,
    TranslationEntry,
    TranslationEntryForList,
    TranslationEntryForParagraph,
} from "./EnhancedTranslator.types"
import TranslatedLink from "./TranslatedLink"

/**
 * Provides an unordered list for a given translation entry
 *
 * @param {object} params Parameter object for TransList
 * @param {TranslationEntryForList} params.entry The translation details describing the list
 * @param {TFunction} params.t TFunction instance passed to top level component that is passed down
 * @returns {ReactElement} An unordered list using content in the translation
 */
export const TransList: FC<{
    entry: TranslationEntryForList
    t: TFunction
}> = ({ entry, t }): ReactElement => {
    /**
     * Renders the required li tags
     *
     * @param {string[]} items The list of items to render as a list
     * @returns {ReactNode} All required li tags rendered as a ReactNode
     */
    function renderList(items: string[]): ReactNode {
        return items.map((item: string, i: number) => {
            return (
                <li key={i}>
                    <Trans
                        t={t}
                        components={{
                            Link: <TranslatedLink />,
                        }}
                    >
                        {item}
                    </Trans>
                </li>
            )
        })
    }

    return (
        <>
            {entry.kind === "numberedlist" ? (
                <ol>{renderList(entry.items)}</ol>
            ) : (
                <ul>{renderList(entry.items)}</ul>
            )}
        </>
    )
}

/**
 * Provides a paragraph for a given translation entry
 *
 * @param {object} params Parameter object passed to Component
 * @param {TranslationEntryForParagraph} params.entry The translation details describing the paragraph
 * @param {TFunction} params.t TFunction instance passed to top level component that is passed down
 * @returns {ReactElement} A paragraph using content in the translation
 */
export const TransParagraph: FC<{
    entry: TranslationEntryForParagraph
    t: TFunction
}> = ({ entry, t }): ReactElement => {
    return (
        <Trans
            t={t}
            components={{
                Link: <TranslatedLink />,
            }}
        >
            <p>{entry.content}</p>
        </Trans>
    )
}

/**
 * An enhanced translator
 *
 * Abstract away from your components whether you are dealing with paragraphs, lists or numbered lists.
 *
 * Just use `EnhancedTranslator` and all will be much easier.
 *
 * @param {EnhancedTranslatorProps} params Parameter object passed to component
 * @param {string} params.i18nKey The translation key to load
 * @param {TFunction} params.t The t function to use to retrieve the translations
 * @param {TOptions} params.options Any options to supply to the t function such as namespace
 * @returns {ReactElement} Rendered out Translations
 */
const EnhancedTranslator: FC<EnhancedTranslatorProps> = ({
    i18nKey,
    t,
    options = {},
}): ReactElement => {
    let entries = t(i18nKey, {
        ...options,
        returnObjects: true,
    }) as TranslationEntry[] | string | TranslationEntry

    if (typeof entries === "string")
        entries = [
            {
                kind: "paragraph",
                content: entries,
            } as TranslationEntryForParagraph,
        ]

    if (!Array.isArray(entries)) entries = [entries]

    return (
        <>
            {entries.map((entry: TranslationEntry | string, i: number) => {
                if (typeof entry === "string")
                    return (
                        <TransParagraph
                            key={i}
                            t={t}
                            entry={{ kind: "paragraph", content: entry }}
                        />
                    )

                switch (entry.kind) {
                    case "list":
                    case "numberedlist":
                        return <TransList key={i} entry={entry} t={t} />
                    default:
                        return <TransParagraph key={i} entry={entry} t={t} />
                }
            })}
        </>
    )
}

export default EnhancedTranslator
